import Vue from 'vue';
import Cookies from 'js-cookie';
import { cloneDeep } from '@/lib/tools';
import { sortObject } from '@/lib/sorter';
import Config from '@/config';
import { changeLanguage } from '@/lib/changeLanguageFn';
import { appEnv } from '@/lib/navigator';

const cookiesPath = { path: Config.cookiePath };
const cookiesExpires = { expires: 0.5 };
const cookiesOption = { ...cookiesPath, ...cookiesExpires };

export const state = {
  // State
  isVisible: false,
  isEnabled: false,

  // Data
  locale: '',
  flags: [], // Global feature flag
  permissions: [], // RBAC
  functions: [], // Controlled by Ripple
};

export const actions = {
  changeVisibleDevTools({ state, commit }) {
    const value = !state.isVisible;

    commit('updateIsVisible', value);
    Cookies.set('wave-devtools-visible', JSON.stringify(value), cookiesOption);
  },

  changeIsEnabled({ state, commit, rootGetters }, value) {
    Cookies.set('wave-devtools-enabled', value, cookiesOption);
    commit('updateIsEnabled', value);

    // Handle Locale
    if (value) {
      changeLanguage(state.locale);
    } else {
      changeLanguage(rootGetters['user/getLocale']);
    }
  },

  changeLocale({ state, commit }, locale) {
    commit('updateLocale', locale);
    Cookies.set('wave-devtools-locale', JSON.stringify(state.locale), cookiesOption);

    changeLanguage(locale);
  },

  changeFlagEnabled({ state, commit }, enabledItems) {
    commit('updateFlagEnabled', enabledItems);
    Cookies.set('wave-devtools-flags', JSON.stringify(state.flags), cookiesOption);
  },

  changePermissionEnabled({ state, commit }, enabledItems) {
    commit('updatePermissionsEnabled', enabledItems);
    Cookies.set('wave-devtools-permissions', JSON.stringify(state.permissions), cookiesOption);
  },

  changeFunctionEnabled({ state, commit }, { key, val }) {
    commit('updateFunctionEnabled', { key, val });
    Cookies.set('wave-devtools-functions', JSON.stringify(state.functions), cookiesOption);
  },

  reset({ commit, dispatch, rootState, rootGetters }) {
    const keys = [
      'wave-devtools-locale',
      'wave-devtools-flags',
      'wave-devtools-permissions',
      'wave-devtools-functions',
    ];

    // Remove cookies
    keys.forEach(key => {
      Cookies.remove(key, cookiesPath);
    });

    // Set real data / Update language - Locale
    const locale = rootGetters['user/getLocale'];
    dispatch('changeLocale', locale);

    // Set real data - Flags
    const flags = rootGetters['globalFlags/getFlags'];
    commit('setFlags', flags);

    // Set real data - Permissions
    const permissions = rootState.rbac.permissions;
    commit('setPermissions', [{ namespace: 'wave', permissions: permissions }]);

    // Set real data - Ripple Functions
    const meta = rootGetters['user/getMeta'];
    commit('setFunctions', { meta: meta });

    return Promise.resolve();
  },
};

export const mutations = {
  updateIsVisible(state, value) {
    if (appEnv === 'localhost' || appEnv === 'dev') {
      state.isVisible = value;
    } else {
      state.isVisible = false;
    }
  },

  updateIsEnabled(state, value) {
    state.isEnabled = value;
  },

  updateLocale(state, locale) {
    state.locale = locale;
  },

  setLocale(state, data) {
    const stored = Cookies.get('wave-devtools-locale');
    if (stored === undefined) {
      const userSetting = data.user_setting;
      state.locale = userSetting ? userSetting.language : 'en';
    } else {
      state.locale = JSON.parse(stored);
    }
  },

  setFlags(state, flags) {
    const stored = Cookies.get('wave-devtools-flags');
    if (stored === undefined) {
      state.flags = cloneDeep(flags);
    } else {
      state.flags = JSON.parse(stored);
    }
  },

  updateFlagEnabled(state, enabledItems) {
    state.flags.forEach(i => {
      i.enabled = !!enabledItems.includes(i.feature_id);
    });
  },

  setPermissions(state, data) {
    const stored = Cookies.get('wave-devtools-permissions');
    if (stored === undefined) {
      const productPermission = data.find(product => {
        return product.namespace === 'wave';
      });

      const permissions = productPermission ? productPermission.permissions : [];

      state.permissions = cloneDeep(permissions);
    } else {
      state.flags = JSON.parse(stored);
    }
  },

  updatePermissionsEnabled(state, enabledItems) {
    Vue.set(state, 'permissions', enabledItems);
  },

  setFunctions(state, data) {
    const stored = Cookies.get('wave-devtools-functions');
    if (stored === undefined) {
      state.functions = cloneDeep(data.meta);
    } else {
      state.functions = JSON.parse(stored);
    }
  },

  updateFunctionEnabled(state, { key, val }) {
    Vue.set(state.functions, key, val);
  },
};

export const getters = {
  getLocale(state) {
    return state.locale ? state.locale : 'en';
  },

  getFlags(state) {
    return state.flags ? sortObject(state.flags, 'feature_name') : [];
  },

  getPermissions(state) {
    return state.permissions ? state.permissions : [];
  },

  getFunctions(state) {
    return state.functions ? state.functions : {};
  },
};
